/**
 * Shared layout dimension constants for the dashboard.
 *
 * WHY THIS FILE EXISTS
 * --------------------
 * Previously, every page/component defined its own local `PAGE_HEADER_HEIGHT`
 * and `DEFAULT_BODY_HEIGHT` constants, each with a slightly different value,
 * and computed scroll-area heights with `calc(100vh - <magic number>px)`.
 * That approach breaks when even one piece of global chrome changes, and makes
 * it impossible to reason about the layout from a single place.
 *
 * The root layout in `dashboard/index.js` now propagates available height via
 * flexbox:
 *
 *   DashboardDrawer (height: 100vh, display: flex)
 *   └── content Box (flex: 1, overflow: hidden, display: flex, flexDirection: column)
 *       └── <main> (flex: 1, display: flex, flexDirection: column, overflow: hidden)
 *           ├── NavBar spacer (flex-shrink: 0, height: NAVBAR_HEIGHT)
 *           └── routes container (flex: 1, overflow: hidden)
 *               └── <Page> (height: 100%) ← pages just use 100% now
 *
 * As a result, page-level wrapper heights no longer need to know about the
 * navbar or content padding. Only the TableContainer components (which need an
 * explicit max-height for MUI sticky headers to scroll) still reference global
 * layout values, and they do so through the constants below.
 *
 * ADD NEW CONSTANTS HERE whenever a dimension is shared across ≥2 components.
 */

// ---------------------------------------------------------------------------
// Global chrome
// ---------------------------------------------------------------------------

/** Height of the fixed top AppBar (NavBar), in pixels. */
export const NAVBAR_HEIGHT = 56;

/**
 * Top and horizontal padding applied to all page content.
 * Comes from `theme.spacing(2.5)` = 2.5 × 8px = 20px.
 */
export const CONTENT_PADDING = 20;

// ---------------------------------------------------------------------------
// Table scroll-area offsets
// ---------------------------------------------------------------------------

/**
 * MUI TablePagination row height (standard density).
 * Used to subtract from a table container's 100% height so the pagination
 * row is not hidden behind the scrollable table body.
 */
export const TABLE_PAGINATION_HEIGHT = 52;

/**
 * Height offset used for `TableContainer maxHeight` in standard full-page
 * list views (BatchList, DataSets, AnnotationManage).
 *
 * Breakdown (all values in px):
 *   NAVBAR_HEIGHT         :  56  (fixed AppBar)
 *   NavBar spacer         :  56  (flex spacer that pushes content past the AppBar)
 *   CONTENT_PADDING × 2  :  40  (top + bottom padding of <main>)
 *   page title area       :  72  (paddingTop 24 + paddingBottom 16 + title ~32)
 *   EnhancedTableToolbar  :  86  (padding-top 24 + padding-bottom 32 + search ~30)
 *   ─────────────────────────────
 *   Total                 : 310
 *
 * Prefer `height: '100%'` on a flex-child container when possible; this
 * constant is a fallback for the TableContainer sticky-header pattern.
 */
export const LIST_TABLE_MAX_HEIGHT_OFFSET = 310;

// ---------------------------------------------------------------------------
// Sidebar widths
// ---------------------------------------------------------------------------

/** Width of the SideBar drawer when fully open, in pixels. */
export const SIDEBAR_WIDTH_OPEN = 248;

/**
 * Width of the SideBar drawer when collapsed (icon-only rail), in pixels.
 * The collapsed icon button is this minus the item's 2×6px horizontal margin,
 * so SideBar's ICON_BOX_PX must stay in sync (52 − 12 = 40).
 */
export const SIDEBAR_WIDTH_COLLAPSED = 52;
